// src/pages/CollectionsPage.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  CardMedia,
  Chip,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Badge,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormLabel,
} from '@mui/material';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { getBestImageUrl } from '../services/s3Service';
import { InternalToolsProductService, SoldProduct, Order, CartItem } from '../types/marketTypes';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import LocalMallIcon from '@mui/icons-material/LocalMall';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from '@mui/icons-material/Delete';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

// API Configuration for Poreia
const API_BASE_URL = 'https://internaltoolsapi-ahdj.onrender.com/api';
const ORG_ID = 'poreia001'; // Poreia's organization ID

// Category mapping for fashion
const categoryMapping: { [key: string]: string } = {
  'tote': 'Totes',
  'sling': 'Sling Bags',
  'handbag': 'Handbags',
  'horseshoe': 'Horseshoe Bags',
  '2 colours': 'Two Tone Bags',
  'clutch': 'Clutches',
  'shoulder-bag': 'Shoulder Bags',
  'crossbody': 'Crossbody Bags',
  'satchel': 'Satchels',
  'bucket': 'Bucket Bags',
  'evening': 'Evening Bags',
  'limited': 'Limited Edition',
  'luxury': 'Luxury',
  'handcrafted': 'Handcrafted',
  'beaded': 'Beaded',
  'leather': 'Leather',
  'strawberry beads': 'Strawberry Beads',
  'bamboo beads': 'Bamboo Beads',
  'petal beads': 'Petal Beads',
  'rounded beads': 'Rounded Beads',
  'small': 'Small Size',
  'medium': 'Medium Size',
  'large': 'Large Size',
  'box beads': 'Box Beads'
};

// Variation interface
interface ProductVariation {
  variationName: string;
  variationImageUrl: string;
  signedUrl?: string;
}

interface ProductWithUrls extends InternalToolsProductService {
  signedImageUrls: string[];
  variations: ProductVariation[];
}

const CollectionsPage: React.FC = () => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [sortBy, setSortBy] = useState('newest');
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  
  // Product states
  const [products, setProducts] = useState<ProductWithUrls[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Cart states
  const [cart, setCart] = useState<CartItem[]>([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [cartItemsCount, setCartItemsCount] = useState(0);
  const [cartTotal, setCartTotal] = useState(0);
  
  // Quick View states
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ProductWithUrls | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedVariation, setSelectedVariation] = useState<ProductVariation | null>(null);
  
  // Order states
  const [orderPlacing, setOrderPlacing] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<string>('Cash on Delivery');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [customerNameNumber, setCustomerNameNumber] = useState('');
  
  // Notification states
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info'
  });

  // Fetch products on component mount
  useEffect(() => {
    fetchProducts();
  }, []);

  // Update cart summary
  useEffect(() => {
    const count = cart.reduce((total, item) => total + item.quantity, 0);
    const total = cart.reduce((total, item) => total + (item.productPriceInUSD * item.quantity), 0);
    setCartItemsCount(count);
    setCartTotal(total);
  }, [cart]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_BASE_URL}/ProductsServices/GetOrgOnlineProductsServices/${ORG_ID}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch products: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // Filter only products with onlineListedFlag = true
      const onlineProducts = data.filter((product: InternalToolsProductService) => 
        product.onlineListedFlag === true && product.productAvailabilityFlag === true
      );
      
      // Load images and variations for each product
      const productsWithUrls = await Promise.all(
        onlineProducts.map(async (product: InternalToolsProductService) => {
          // Load main product images
          const signedImageUrls = await Promise.all(
            (product.productPictures || []).map(async (picture) => {
              try {
                return await getBestImageUrl(picture);
              } catch (error) {
                console.error('Failed to load product image:', error);
                return '';
              }
            })
          );
          
          // Load variation images
          const variationsWithUrls = await Promise.all(
            (product.variations || []).map(async (variation: any) => {
              try {
                const signedUrl = await getBestImageUrl(variation.variationImageUrl);
                return {
                  variationName: variation.variationName,
                  variationImageUrl: variation.variationImageUrl,
                  signedUrl
                };
              } catch (error) {
                console.error('Failed to load variation image:', error);
                return {
                  variationName: variation.variationName,
                  variationImageUrl: variation.variationImageUrl,
                  signedUrl: ''
                };
              }
            })
          );
          
          return {
            ...product,
            signedImageUrls: signedImageUrls.filter(url => url !== ''),
            variations: variationsWithUrls
          };
        })
      );
      
      setProducts(productsWithUrls);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load products');
      console.error('Error fetching products:', err);
      setProducts([]); // Empty array instead of mock data
    } finally {
      setLoading(false);
    }
  };

  // Get product category based on tags
  const getProductCategories = (product: InternalToolsProductService): string[] => {
  if (!product.productTag) return ['Accessories'];
  
  const tags = product.productTag.toLowerCase().split(',').map(tag => tag.trim());
  const categories: string[] = [];
  
  // First: Check for exact matches in mapping
  tags.forEach(tag => {
    if (categoryMapping[tag]) {
      if (!categories.includes(categoryMapping[tag])) {
        categories.push(categoryMapping[tag]);
      }
    }
  });
  
  // Second: Check for partial matches
  tags.forEach(tag => {
    Object.entries(categoryMapping).forEach(([key, value]) => {
      if (tag.includes(key) || key.includes(tag)) {
        if (!categories.includes(value)) {
          categories.push(value);
        }
      }
    });
  });
  
  return categories.length > 0 ? categories : ['Accessories'];
};

// Filter products with multi-category support
const filteredProducts = products.filter(product => {
  const matchesSearch = product.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       product.productDescription.toLowerCase().includes(searchTerm.toLowerCase());
  
  if (selectedCategory === 'all') return matchesSearch;
  
  const productCategories = getProductCategories(product);
  const matchesCategory = productCategories.includes(selectedCategory);
  
  return matchesSearch && matchesCategory;
});

  // Sort products
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'price-low':
        return a.productPriceInUSD - b.productPriceInUSD;
      case 'price-high':
        return b.productPriceInUSD - a.productPriceInUSD;
      case 'newest':
      default:
        return 0; // Add timestamp field if available
    }
  });

  // Get unique categories from products
  const getCategories = () => {
  const categoryCounts: { [key: string]: number } = {};
  
  // Count how many products belong to each category
  products.forEach(product => {
    const productCats = getProductCategories(product);
    productCats.forEach(cat => {
      categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
    });
  });
  
  return Object.entries(categoryCounts).map(([name, count]) => ({
    id: name, // Use the category name as ID
    name: name,
    count: count
  }));
};

const categories = [
  { id: 'all', name: 'All Collections', count: products.length },
  ...getCategories()
];

  // Cart functions
  const addToCart = (product: ProductWithUrls, variation?: ProductVariation) => {
  // Use first available image
  const productImage = product.signedImageUrls.length > 0 
    ? product.signedImageUrls[0] 
    : (product.variations.length > 0 && product.variations[0].signedUrl) || '';
  
  const variationName = variation ? variation.variationName : '';
  
  setCart(prevCart => {
    // Create a unique cart item ID that includes variation
    const cartItemId = `${product._id}-${variationName}`;
    const existingItem = prevCart.find(item => 
      item.productId === cartItemId
    );
    
    if (existingItem) {
      return prevCart.map(item =>
        item.productId === cartItemId
          ? { ...item, quantity: item.quantity + 1 }
          : item
      );
    } else {
      return [...prevCart, {
        productId: cartItemId,
        uniqueProductId: product.uniqueProductId,
        productName: `${product.productName}${variationName ? ` - ${variationName}` : ''}`,
        productPriceInUSD: product.productPriceInUSD,
        quantity: 1,
        productImage: variation?.signedUrl || productImage,
        quantityMetric: product.quantityMetric,
        productType: product.productType,
        productDescription: product.productDescription,
        Variation: variationName // Add variation property
      }];
    }
  });
  
  showNotification('Product added to cart', 'success');
};

  const removeFromCart = (productId: string) => {
    setCart(prevCart => prevCart.filter(item => item.productId !== productId));
    showNotification('Product removed from cart', 'info');
  };

  const updateQuantity = (productId: string, newQuantity: number) => {
    if (newQuantity < 1) {
      removeFromCart(productId);
      return;
    }
    
    setCart(prevCart =>
      prevCart.map(item =>
        item.productId === productId
          ? { ...item, quantity: newQuantity }
          : item
      )
    );
  };

  // Quick View functions
  const openQuickView = (product: ProductWithUrls) => {
    setSelectedProduct(product);
    setSelectedImageIndex(0);
    setSelectedVariation(null);
    setQuickViewOpen(true);
  };

  const handleNextImage = () => {
    if (selectedProduct) {
      const totalImages = selectedProduct.signedImageUrls.length;
      setSelectedImageIndex((prev) => (prev + 1) % totalImages);
    }
  };

  const handlePrevImage = () => {
    if (selectedProduct) {
      const totalImages = selectedProduct.signedImageUrls.length;
      setSelectedImageIndex((prev) => (prev - 1 + totalImages) % totalImages);
    }
  };

  const handleSelectVariation = (variation: ProductVariation) => {
    setSelectedVariation(variation);
    // If variation has an image, we could show it as the main image
    if (variation.signedUrl) {
      // For simplicity, we'll just select the variation
      // In a more complex implementation, you might want to switch the main image
    }
  };

  // Order functions
  const generateOrderId = (): string => {
    const timestamp = new Date().getTime();
    const random = Math.floor(Math.random() * 10000);
    return `ORD-${timestamp}-${random}`;
  };

  const placeOrder = async () => {
    if (cart.length === 0) {
      showNotification('Cart is empty', 'error');
      return;
    }

    if (!deliveryAddress.trim()) {
      showNotification('Please enter delivery address', 'error');
      return;
    }

    if (!customerNameNumber.trim()) {
      showNotification('Please enter customer name and phone number', 'error');
      return;
    }

    setOrderPlacing(true);
    setOrderError(null);

    try {
      // Convert cart items to SoldProduct array
      const cartList: SoldProduct[] = cart.map(item => {
        // Extract original product ID (remove variation suffix)
        const originalProductId = item.productId.split('-')[0];
        const originalProduct = products.find(p => p._id === originalProductId);
        
        return {
          wellnessProductId: '',
          uniqueProductId: item.uniqueProductId,
          wellnessProductTag: originalProduct?.productTag || 'poreia-collections',
          productName: item.productName,
          provider: 'poreia',
          productPrice: item.productPriceInUSD,
          productDescription: item.productDescription,
          officialTimeTakenInMins: 0,
          variation: item.Variation || 'standard',
          wellnessPillar: '',
          productType: item.productType,
          payers: [],
          productQuantityBought: item.quantity,
          quantityMetric: item.quantityMetric,
          valueAddedTaxOrEquivalentPercentage: originalProduct?.valueAddedTaxOrEquivalentPercentage || 0.15
        };
      });

      // Create order object
      const order: Order = {
        orderId: generateOrderId(),
        orgUniqueId: ORG_ID,
        orderStatus: 'pending',
        customerUserName: customerNameNumber,
        providerUserName: 'poreia',
        orderDateTimeStamp: new Date().toISOString(),
        paymentStatus: 'pending',
        paymentMethodPreferred: paymentMethod,
        saleUniqueId: ``,
        orderPriority: 'normal',
        deliveryAddress: deliveryAddress,
        deliveryDateTimeStamp: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
        deliveryStatus: 'scheduled',
        cartList: cartList
      };

      // Send order to API
      const response = await fetch(`${API_BASE_URL}/Services/PlaceOrder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(order)
      });

      if (!response.ok) {
        let errorDetails = '';
        try {
          const errorData = await response.json();
          errorDetails = JSON.stringify(errorData);
        } catch (e) {
          errorDetails = await response.text();
        }
        throw new Error(`Failed to place order: ${response.status} ${response.statusText}. Details: ${errorDetails}`);
      }

      const placedOrder = await response.json();
      console.log('Order placed successfully:', placedOrder);
      
      // Clear cart and show success
      setCart([]);
      setOrderSuccess(true);
      setCheckoutOpen(false);
      showNotification('Order placed successfully!', 'success');
      
      // Reset after 3 seconds
      setTimeout(() => {
        setOrderSuccess(false);
      }, 3000);

    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to place order';
      setOrderError(errorMsg);
      showNotification(`Failed to place order: ${errorMsg}`, 'error');
      console.error('Error placing order:', err);
    } finally {
      setOrderPlacing(false);
    }
  };

  // Show notification
  const showNotification = (message: string, severity: 'success' | 'error' | 'info') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  return (
    <Box>
      <Navbar />
      
      {/* Hero Banner */}
      <Box
        sx={{
          height: '60vh',
          backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(images/collections.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ color: 'white', textAlign: 'center' }}>
            <Typography
              variant="h1"
              component={motion.h1}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              sx={{
                fontSize: { xs: '2.5rem', md: '4rem' },
                fontWeight: 400,
                letterSpacing: '0.1em',
                mb: 2,
                fontFamily: "'Playfair Display', serif",
              }}
            >
              Collections
            </Typography>
            
            <Typography
              variant="h5"
              component={motion.h5}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              sx={{
                fontWeight: 300,
                letterSpacing: '0.2em',
                mb: 4,
                fontFamily: "'Cormorant Garamond', serif",
                maxWidth: 600,
                mx: 'auto',
              }}
            >
              Handcrafted luxury bags that carry African heritage and support artisan communities
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Collections Content */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 4 }}>
            {error} - Please check your API connection.
          </Alert>
        )}

        {/* Filters and Search */}
        <Box sx={{ mb: 6 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: { xs: '100%', md: 'auto' } }}>
              <IconButton
                onClick={() => setFilterDrawerOpen(true)}
                sx={{ display: { md: 'none' } }}
              >
                <FilterListIcon />
              </IconButton>
              
              <TextField
                placeholder="Search collections..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                sx={{ width: { xs: '100%', md: 300 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: { xs: '100%', md: 'auto' } }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Sort by</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort by"
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <MenuItem value="newest">Newest</MenuItem>
                  <MenuItem value="price-low">Price: Low to High</MenuItem>
                  <MenuItem value="price-high">Price: High to Low</MenuItem>
                </Select>
              </FormControl>

              <Button
                variant="outlined"
                onClick={() => setFilterDrawerOpen(true)}
                startIcon={<FilterListIcon />}
                sx={{ display: { xs: 'none', md: 'flex' } }}
              >
                Filters
              </Button>

              <IconButton 
                onClick={() => setCartOpen(true)}
                sx={{ position: 'relative' }}
              >
                <Badge badgeContent={cartItemsCount} color="primary">
                  <ShoppingCartIcon />
                </Badge>
              </IconButton>
            </Box>
          </Box>

          {/* Category Chips */}
          <Box sx={{ mt: 4, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {categories.map((category) => (
              <Chip
                key={category.id}
                label={`${category.name}`}
                onClick={() => setSelectedCategory(category.id)}
                variant={selectedCategory === category.id ? 'filled' : 'outlined'}
                sx={{
                  borderColor: selectedCategory === category.id ? 'primary.main' : 'grey.300',
                  backgroundColor: selectedCategory === category.id ? 'primary.main' : 'transparent',
                  color: selectedCategory === category.id ? 'white' : 'text.primary',
                  '&:hover': {
                    backgroundColor: selectedCategory === category.id ? 'primary.dark' : 'grey.100',
                  },
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Products Grid */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress />
          </Box>
        ) : sortedProducts.length === 0 ? (
          <Paper sx={{ p: 8, textAlign: 'center', borderRadius: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              No products found
            </Typography>
            <Typography color="text.secondary">
              Try adjusting your search or filters
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
            gap: 4,
          }}>
            {sortedProducts.map((product, index) => (
              <motion.div
                key={product._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -8 }}
              >
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    border: '1px solid',
                    borderColor: 'grey.200',
                    borderRadius: 0,
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                      borderColor: 'primary.light',
                    },
                  }}
                >
                  <Box sx={{ position: 'relative' }}>
                    <CardMedia
                      component="img"
                      sx={{ 
                        width: '100%',
                        height: 300,
                        objectFit: 'cover' 
                      }}
                      image={product.signedImageUrls[0] || ''}
                      alt={product.productName}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = '';
                      }}
                    />
                    
                    {/* Tags */}
                    {product.productTag && (
<Box sx={{ position: 'absolute', top: 12, left: 12, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
  {getProductCategories(product).slice(0, 2).map((category, index) => (
    <Chip
      key={index}
      label={category}
      size="small"
      sx={{
        color: 'white',
        fontWeight: 600,
        fontSize: '0.65rem'
      }}
    />
  ))}
  {getProductCategories(product).length > 2 && (
    <Chip
      label={`+${getProductCategories(product).length - 2}`}
      size="small"
      sx={{
        color: 'white',
        fontWeight: 600,
        fontSize: '0.65rem'
      }}
    />
  )}
  </Box>
                    )}
                    
                    {product.quantityAvailable < 5 && product.quantityAvailable > 0 && (
                      <Chip
                        label="Few Units Left"
                        size="small"
                        color="warning"
                        sx={{
                          position: 'absolute',
                          top: 12,
                          right: 12,
                          fontWeight: 600
                        }}
                      />
                    )}

                    {product.quantityAvailable <=  0 && (
                      <Chip
                        label="Out of Stock"
                        size="small"
                        color="error"
                        sx={{
                          position: 'absolute',
                          top: 12,
                          right: 12,
                          fontWeight: 600
                        }}
                      />
                    )}
                    
                    {/* Quick View Overlay */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.7)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0,
                        transition: 'opacity 0.3s ease',
                        '&:hover': {
                          opacity: 1,
                        },
                      }}
                    >
                      <Button
                        variant="contained"
                        onClick={() => openQuickView(product)}
                        startIcon={<LocalMallIcon />}
                        sx={{
                          backgroundColor: 'secondary.main',
                          '&:hover': {
                            backgroundColor: 'secondary.dark',
                          },
                        }}
                      >
                        Quick View
                      </Button>
                    </Box>
                  </Box>
                  
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontFamily: "'Playfair Display', serif",
                        mb: 1,
                        fontWeight: 500,
                      }}
                    >
                      {product.productName}
                    </Typography>
                    
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        mb: 2,
                        fontSize: '0.875rem',
                        lineHeight: 1.4,
                        height: '3rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}
                    >
                      {product.productDescription}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 'auto' }}>
                      <Typography
                        variant="h6"
                        sx={{
                          color: 'primary.main',
                          fontWeight: 600,
                        }}
                      >
                        ${product.productPriceInUSD.toFixed(2)}
                      </Typography>
                    </Box>

                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Available: {product.quantityAvailable} {product.quantityMetric}
                    </Typography>
                  </CardContent>
                  
                  <CardActions sx={{ p: 3, pt: 0 }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => addToCart(product)}
                      disabled={product.quantityAvailable === 0}
                      sx={{
                        borderColor: 'primary.main',
                        color: 'primary.main',
                        '&:hover': {
                          borderColor: 'primary.dark',
                          backgroundColor: 'rgba(0,0,0,0.02)',
                        },
                      }}
                    >
                      {product.quantityAvailable === 0 ? 'Out of Stock' : 'Add to Cart'}
                    </Button>
                  </CardActions>
                </Card>
              </motion.div>
            ))}
          </Box>
        )}
      </Container>

      {/* Quick View Dialog */}
      <Dialog
        open={quickViewOpen}
        onClose={() => setQuickViewOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        {selectedProduct && (
          <>
            <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {selectedProduct.productName}
              <IconButton onClick={() => setQuickViewOpen(false)}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
                {/* Product Images */}
                <Box sx={{ flex: 1, position: 'relative' }}>
                  {selectedProduct.signedImageUrls.length > 0 && (
                    <>
                      <Box sx={{ position: 'relative', width: '100%', height: 400, overflow: 'hidden' }}>
                        <img
                          src={selectedProduct.signedImageUrls[selectedImageIndex]}
                          alt={selectedProduct.productName}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: 8
                          }}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = '';
                          }}
                        />
                        
                        {selectedProduct.signedImageUrls.length > 1 && (
                          <>
                            <IconButton
                              onClick={handlePrevImage}
                              sx={{
                                position: 'absolute',
                                left: 10,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                backgroundColor: 'rgba(255,255,255,0.8)',
                                '&:hover': { backgroundColor: 'white' }
                              }}
                            >
                              <ArrowBackIosIcon />
                            </IconButton>
                            <IconButton
                              onClick={handleNextImage}
                              sx={{
                                position: 'absolute',
                                right: 10,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                backgroundColor: 'rgba(255,255,255,0.8)',
                                '&:hover': { backgroundColor: 'white' }
                              }}
                            >
                              <ArrowForwardIosIcon />
                            </IconButton>
                          </>
                        )}
                      </Box>
                      
                      {/* Thumbnail Images */}
                      {selectedProduct.signedImageUrls.length > 1 && (
                        <Box sx={{ display: 'flex', gap: 1, mt: 2, overflowX: 'auto' }}>
                          {selectedProduct.signedImageUrls.map((url, index) => (
                            <Box
                              key={index}
                              onClick={() => setSelectedImageIndex(index)}
                              sx={{
                                width: 60,
                                height: 60,
                                borderRadius: 1,
                                overflow: 'hidden',
                                cursor: 'pointer',
                                border: selectedImageIndex === index ? `2px solid ${theme.palette.primary.main}` : '1px solid #ddd',
                                opacity: selectedImageIndex === index ? 1 : 0.7,
                                '&:hover': { opacity: 1 }
                              }}
                            >
                              <img
                                src={url}
                                alt={`Thumbnail ${index + 1}`}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            </Box>
                          ))}
                        </Box>
                      )}
                    </>
                  )}
                </Box>
                
                {/* Product Details */}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                    ${selectedProduct.productPriceInUSD.toFixed(2)}
                  </Typography>
                  
                  <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                    {selectedProduct.productDescription}
                  </Typography>
                  
                  {/* Variations */}
                  {selectedProduct.variations.length > 0 && (
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                        Variations
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        {selectedProduct.variations.map((variation, index) => (
                          <Box
                            key={index}
                            onClick={() => handleSelectVariation(variation)}
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: 1,
                              cursor: 'pointer',
                              p: 1,
                              borderRadius: 1,
                              border: selectedVariation?.variationName === variation.variationName 
                                ? `2px solid ${theme.palette.primary.main}`
                                : '1px solid #ddd',
                              '&:hover': { backgroundColor: '#f5f5f5' }
                            }}
                          >
                            {variation.signedUrl && (
                              <img
                                src={variation.signedUrl}
                                alt={variation.variationName}
                                style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4 }}
                              />
                            )}
                            <Typography variant="caption">
                              {variation.variationName}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                  
                  <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => {
                        addToCart(selectedProduct, selectedVariation || undefined);
                        setQuickViewOpen(false);
                      }}
                      disabled={selectedProduct.quantityAvailable === 0}
                      sx={{ py: 1.5 }}
                    >
                      Add to Cart
                    </Button>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => {
                        addToCart(selectedProduct, selectedVariation || undefined);
                        setQuickViewOpen(false);
                        setCartOpen(true);
                      }}
                      disabled={selectedProduct.quantityAvailable === 0}
                      sx={{ py: 1.5 }}
                    >
                      Buy Now
                    </Button>
                  </Box>
                </Box>
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* Shopping Cart Drawer */}
      <Drawer
        anchor="right"
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 400 } }
        }}
      >
        <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Shopping Cart
            </Typography>
            <IconButton onClick={() => setCartOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          {cart.length === 0 ? (
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <Box sx={{ fontSize: 80, color: 'text.secondary', mb: 2, opacity: 0.5 }}>🛒</Box>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Your cart is empty
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Add some products to get started
              </Typography>
              <Button
                variant="contained"
                onClick={() => setCartOpen(false)}
              >
                Continue Shopping
              </Button>
            </Box>
          ) : (
            <>
              <List sx={{ flexGrow: 1, overflow: 'auto' }}>
                {cart.map((item) => (
                  <React.Fragment key={item.productId}>
                    <ListItem sx={{ px: 0 }}>
                      <Box sx={{ display: 'flex', width: '100%', gap: 2 }}>
                        <Box sx={{ 
                          width: 80, 
                          height: 80, 
                          borderRadius: 2,
                          overflow: 'hidden',
                          flexShrink: 0
                        }}>
                          <img 
                            src={item.productImage} 
                            alt={item.productName}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = '';
                            }}
                          />
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {item.productName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            ${item.productPriceInUSD.toFixed(2)} each
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <IconButton 
                                size="small" 
                                onClick={() => updateQuantity(item.productId, item.quantity - 1)}
                              >
                                <RemoveIcon />
                              </IconButton>
                              <Typography sx={{ minWidth: 30, textAlign: 'center' }}>
                                {item.quantity}
                              </Typography>
                              <IconButton 
                                size="small" 
                                onClick={() => updateQuantity(item.productId, item.quantity + 1)}
                              >
                                <AddIcon />
                              </IconButton>
                            </Box>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              ${(item.productPriceInUSD * item.quantity).toFixed(2)}
                            </Typography>
                            <IconButton 
                              size="small" 
                              onClick={() => removeFromCart(item.productId)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </Box>
                      </Box>
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))}
              </List>

              {/* Cart Summary */}
              <Box sx={{ mt: 3, pt: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography>Subtotal</Typography>
                  <Typography>${cartTotal.toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Total</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>${cartTotal.toFixed(2)}</Typography>
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={() => {
                    setCartOpen(false);
                    setCheckoutOpen(true);
                  }}
                  sx={{ 
                    mb: 2,
                    py: 1.5,
                    fontWeight: 600,
                    bgcolor: theme.palette.primary.main,
                    '&:hover': {
                      bgcolor: theme.palette.primary.dark
                    }
                  }}
                >
                  Proceed to Checkout
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => setCartOpen(false)}
                >
                  Continue Shopping
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Drawer>

      {/* Checkout Dialog */}
      <Dialog
        open={checkoutOpen}
        onClose={() => !orderPlacing && setCheckoutOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Checkout
        </DialogTitle>
        
        <DialogContent>
          {orderError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {orderError}
            </Alert>
          )}
          
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Order Summary
            </Typography>
            
            <List dense>
              {cart.map((item) => (
                <ListItem key={item.productId} sx={{ px: 0 }}>
                  <ListItemText 
                    primary={item.productName}
                    secondary={`${item.quantity} × $${item.productPriceInUSD.toFixed(2)}`}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    ${(item.productPriceInUSD * item.quantity).toFixed(2)}
                  </Typography>
                </ListItem>
              ))}
            </List>
            
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Total</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>${cartTotal.toFixed(2)}</Typography>
            </Box>
            
            {/* Customer Name & Phone Number */}
            <TextField
              fullWidth
              label="Customer Name & PHONE NUMBER"
              value={customerNameNumber}
              onChange={(e) => setCustomerNameNumber(e.target.value)}
              multiline
              rows={2}
              required
              sx={{ mb: 3 }}
              placeholder="Enter details failure to put your phone number your order will be cancelled"
            />

            {/* Delivery Address */}
            <TextField
              fullWidth
              label="Delivery Address"
              value={deliveryAddress}
              onChange={(e) => setDeliveryAddress(e.target.value)}
              multiline
              rows={2}
              required
              sx={{ mb: 3 }}
              placeholder="Enter your full delivery address"
            />
            
            {/* Payment Method */}
            <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
              <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>
                Payment Method
              </FormLabel>
              <RadioGroup
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
              >
                <FormControlLabel
                  value="Cash on Delivery"
                  control={<Radio />}
                  label="Cash on Delivery"
                />
                <FormControlLabel
                  value="Credit Card"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Credit Card
                      <Chip
                        label="Coming Soon"
                        size="small"
                        sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                      />
                    </Box>
                  }
                  disabled
                />
                <FormControlLabel
                  value="PayPal"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      PayPal
                      <Chip
                        label="Coming Soon"
                        size="small"
                        sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                      />
                    </Box>
                  }
                  disabled
                />
              </RadioGroup>
            </FormControl>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              Only Cash on Delivery is available at the moment. Other payment methods coming soon.
            </Alert>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={() => setCheckoutOpen(false)}
            disabled={orderPlacing}
          >
            Cancel
          </Button>
          
          <Button
            variant="contained"
            onClick={placeOrder}
            disabled={orderPlacing || !deliveryAddress.trim()}
            sx={{
              bgcolor: theme.palette.primary.main,
              '&:hover': { bgcolor: theme.palette.primary.dark }
            }}
          >
            {orderPlacing ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                Placing Order...
              </>
            ) : 'Place Order'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filter Drawer */}
      <Drawer
        anchor="left"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        PaperProps={{
          sx: { width: 300, p: 3 },
        }}
      >
        <Typography variant="h6" sx={{ mb: 3 }}>
          Filters
        </Typography>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              value={selectedCategory}
              label="Category"
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl fullWidth>
            <InputLabel>Sort by</InputLabel>
            <Select
              value={sortBy}
              label="Sort by"
              onChange={(e) => setSortBy(e.target.value)}
            >
              <MenuItem value="newest">Newest</MenuItem>
              <MenuItem value="price-low">Price: Low to High</MenuItem>
              <MenuItem value="price-high">Price: High to Low</MenuItem>
            </Select>
          </FormControl>
          
          <Button
            variant="contained"
            onClick={() => setFilterDrawerOpen(false)}
            sx={{ mt: 2 }}
          >
            Apply Filters
          </Button>
        </Box>
      </Drawer>

      {/* Order Success Alert */}
      {orderSuccess && (
        <Alert 
          severity="success" 
          sx={{ 
            mt: 4,
            position: 'fixed',
            top: 20,
            right: 20,
            zIndex: 9999,
            width: 400
          }}
          onClose={() => setOrderSuccess(false)}
        >
          Order placed successfully! Check your account to track order.
        </Alert>
      )}

      <Footer />
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CollectionsPage;